import { useLazyQuery, useQuery } from '@apollo/client';
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, Container, InputAdornment, TextField, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeItem2 } from '@mui/x-tree-view/TreeItem2';
import { useTreeItem2Utils } from '@mui/x-tree-view/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GET_ELEMENTS, GET_ALL_ELEMENTS } from '../graphql/getElements';

let tmpNextId = 999999999999

function CustomLabel({ children, className, secondaryLabel, projectId }) {
    const navigate = useNavigate();
    return (
        <div className={className} style={{ width: '100%', padding: 4 }}>
            <Typography>{children}
                <Button
                    endIcon={<KeyboardDoubleArrowRightIcon />}
                    variant="outlined" size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/welds/${projectId}/${secondaryLabel.id}`);
                    }}
                    sx={{ marginRight: "0px", float: "right" }}
                >
                    Welds
                </Button>
            </Typography>
        </div>
    );
}

const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
    const { publicAPI } = useTreeItem2Utils({
        itemId: props.itemId,
        children: props.children,
    });
    const item = publicAPI.getItem(props.itemId);
    return (
        <TreeItem2
            {...props}
            ref={ref}
            slots={{ label: CustomLabel }}
            slotProps={{ label: { secondaryLabel: item, projectId: props.projectId } }}
        />
    );
});

// Build ancestor id set for all nodes matching the search
function getAncestorIds(matchingIds, allNodes) {
    const byId = {}
    allNodes.forEach(n => { byId[String(n.id)] = n })
    const result = new Set()
    matchingIds.forEach(id => {
        let cur = byId[String(id)]
        while (cur && cur.parent_id) {
            result.add(String(cur.parent_id))
            cur = byId[String(cur.parent_id)]
        }
    })
    return result
}

// Build tree from flat list filtered to visible ids
function buildFilteredTree(allNodes, visibleIds, projectId) {
    const byParent = {}
    allNodes.forEach(n => {
        const pid = String(n.parent_id || projectId)
        if (!byParent[pid]) byParent[pid] = []
        byParent[pid].push(n)
    })

    function buildNode(node) {
        const children = (byParent[String(node.id)] || [])
            .filter(c => visibleIds.has(String(c.id)))
            .map(buildNode)
        return { id: String(node.id), label: node.name, children }
    }

    return (byParent[String(projectId)] || [])
        .filter(n => visibleIds.has(String(n.id)))
        .map(buildNode)
}

const Elements = () => {
    const [projectId] = useState(Number(useParams().projectId))
    const [search, setSearch] = useState('')

    // Lazy tree (original behaviour when no search)
    const { data, loading, error } = useQuery(GET_ELEMENTS, {
        variables: { projectId },
    })
    const [getElements] = useLazyQuery(GET_ELEMENTS)

    // Full flat tree for search
    const { data: allData, loading: allLoading } = useQuery(GET_ALL_ELEMENTS, {
        variables: { projectId },
    })

    const allNodes = useMemo(() => allData?.elements?.list || [], [allData])

    // --- lazy tree state (used when search is empty) ---
    let [tree, setTree] = useState([])
    let element = {}

    if (!loading && !error && data && tree.length === 0) {
        data?.elements?.list.forEach((item) => {
            tree = createNodeTree(item, tree)
        })
    }

    function createNodeTree(item, t) {
        return Number(item.children_count) > 0
            ? [...t, { id: item.id + '', label: item.name, children: [{ id: tmpNextId++ + '', label: '' }] }]
            : [...t, { id: item.id + '', label: item.name, children: [] }]
    }

    function getSubMenuItem(subMenuItems, id) {
        if (subMenuItems.length > 0) {
            subMenuItems.forEach((item) => {
                if (Number(item.id) === Number(id)) {
                    element = item
                } else if (item.children?.length > 0) {
                    getSubMenuItem(item.children, id)
                }
            })
        }
        return element
    }

    function handleTree(elementId) {
        const childTree = [...tree]
        let artwork = getSubMenuItem(childTree, elementId)
        if (artwork.children?.length > 0) {
            getElements({ variables: { projectId: Number(elementId) } }).then((response) => {
                let nextChildTree = []
                response.data?.elements?.list.forEach((item) => {
                    nextChildTree = createNodeTree(item, nextChildTree)
                })
                if (artwork.children[0].label === '') {
                    artwork.children = nextChildTree
                }
                setTree(childTree)
            })
        }
    }

    // --- filtered tree (used when search is not empty) ---
    const { filteredTree, expandedIds } = useMemo(() => {
        if (!search.trim() || allNodes.length === 0) return { filteredTree: [], expandedIds: [] }

        const q = search.trim().toLowerCase()
        const matching = allNodes.filter(n => n.name.toLowerCase().includes(q))
        const matchingIds = new Set(matching.map(n => String(n.id)))
        const ancestorIds = getAncestorIds(matchingIds, allNodes)
        const visibleIds = new Set([...matchingIds, ...ancestorIds])

        return {
            filteredTree: buildFilteredTree(allNodes, visibleIds, projectId),
            expandedIds: Array.from(ancestorIds)
        }
    }, [search, allNodes, projectId])

    const isSearching = search.trim().length > 0

    if (loading) return <Box sx={{ width: '100%' }}><LinearProgress /></Box>
    if (error) return `Error! ${error.message}`

    return (
        <Container sx={{ paddingTop: 4 }}>
            <Typography variant="h5" gutterBottom>Elements tree in Project</Typography>
            <TextField
                label="Search"
                size="small"
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ mb: 2, width: 300 }}
                InputProps={{
                    endAdornment: search ? (
                        <InputAdornment position="end">
                            <ClearIcon sx={{ cursor: 'pointer', fontSize: 18 }} onClick={() => setSearch('')} />
                        </InputAdornment>
                    ) : null
                }}
            />
            {isSearching && allLoading && <Box sx={{ width: '100%' }}><LinearProgress /></Box>}
            {isSearching ? (
                filteredTree.length === 0
                    ? <Typography color="text.secondary">No results found</Typography>
                    : <RichTreeView
                        items={filteredTree}
                        expandedItems={expandedIds}
                        slots={{ item: CustomTreeItem }}
                        slotProps={{ item: { projectId } }}
                    />
            ) : (
                <RichTreeView
                    items={tree}
                    onItemClick={(event, itemId) => handleTree(itemId)}
                    slots={{ item: CustomTreeItem }}
                    slotProps={{ item: { projectId } }}
                />
            )}
        </Container>
    )
}

export default Elements;
